import { STROLLER_INFINITE_RECLINE } from 'mk/autogenerated/translations/index.a047eb7a6653bc7c1441c68cc9a806ac'
import {
    BooleanFeature,
    CountriesFeature,
    Feature,
    InfiniteNumericListFeature,
    MinMaxDimensionsFeature,
    NumericFeature,
    NumericListFeature,
    NumericOptionalRangeFeature,
    WidthDepthFeature,
} from 'mk2/apps/strollers/features/basic';
import {
    ArtificialWheelTypeFeature,
    BackSupportPositioningTypeFeature,
    BumperBarTypeFeature,
    CentralBrakeSystemFeature,
    FoldingSystemFeature,
    HandleAdjustmentTypeFeature,
    MaterialFeature,
    OrNumericListFeature,
    SuspendedWheelsFeature,
    SuspensionTypeFeature,
    TypeFeature,
    WheelTypeFeature,
} from 'mk2/apps/strollers/features/options';
import {
    CarrycotCanBeAttached,
    CarSeatCanBeAttached,
    CarSeatHasToBeBought,
    SuppliedChangingBagFeature,
    VariantBooleanListFeature,
    VariantBooleanListJsonFeature,
    VariantListWidthDepthFeature,
} from 'mk2/apps/strollers/features/other';

interface FeaturesList {
    [K: string]: Feature;
}

export const FEATURES_LIST: FeaturesList = {
    adjustable_handle: new BooleanFeature('adjustable_handle'),
    all_wheels_are_double: new BooleanFeature('all_wheels_are_double'),
    artificial_wheel_type: new ArtificialWheelTypeFeature('artificial_wheel_type'),
    autolock: new BooleanFeature('autolock'),
    back_support_can_be_extended: new BooleanFeature('back_support_can_be_extended'),
    back_support_can_be_in_lying_position: new BooleanFeature('back_support_can_be_in_lying_position'),
    back_support_can_be_positioned: new BooleanFeature('back_support_can_be_positioned'),
    back_support_length_in_cm: new NumericOptionalRangeFeature('back_support_length_in_cm'),
    back_support_positioning_type: new BackSupportPositioningTypeFeature('back_support_positioning_type'),
    back_support_positions: new InfiniteNumericListFeature('back_support_positions_l', { infinityName: STROLLER_INFINITE_RECLINE }),
    big_shopping_basket: new BooleanFeature('big_shopping_basket'),
    bucket_seat: new BooleanFeature('bucket_seat'),
    bucket_second_seat: new BooleanFeature('bucket_second_seat'),
    builtin_speaker: new BooleanFeature('builtin_speaker'),
    bumper_bar: new BooleanFeature('bumper_bar'),
    bumper_bar_can_be_positioned: new BooleanFeature('bumper_bar_can_be_positioned'),
    bumper_bar_can_be_removed: new BooleanFeature('bumper_bar_can_be_removed'),
    bumper_bar_type: new BumperBarTypeFeature('bumper_bar_type'),
    can_be_converted_to_a_double: new BooleanFeature('can_be_converted_to_a_double'),
    can_be_converted_to_a_single: new BooleanFeature('can_be_converted_to_a_single'),
    can_be_pulled_behind: new BooleanFeature('can_be_pulled_behind'),
    car_seat_can_be_attached: new CarSeatCanBeAttached('car_seat_can_be_attached'),
    car_seat_compatibility_most_brands: new BooleanFeature('car_seat_compatibility_most_brands'),
    car_seat_compatibility_roemer: new BooleanFeature('car_seat_compatibility_roemer'),
    car_seat_has_to_be_bought: new CarSeatHasToBeBought('car_seat_has_to_be_bought'),
    carrycot_can_be_attached: new CarrycotCanBeAttached('carrycot_can_be_attached'),
    carrycot_dims_in_cm: new VariantListWidthDepthFeature('carrycot_dims_in_cm'),
    carrycot_has_handle: new VariantBooleanListJsonFeature('carrycot_has_handle'),
    carrycot_has_ventilation: new VariantBooleanListFeature('carrycot_has_ventilation'),
    carrycot_usable_in_car: new VariantBooleanListFeature('carrycot_usable_in_car'),
    carrycot_with_cradle: new VariantBooleanListFeature('carrycot_with_cradle'),
    central_brake_present: new BooleanFeature('central_brake_present'),
    central_brake_system: new CentralBrakeSystemFeature('central_brake_system'),
    checked_baggage: new BooleanFeature('checked_baggage'),
    combo_only: new BooleanFeature('combo_only'),
    cupholder_possibility: new BooleanFeature('cupholder_possibility'),
    discontinued_year: new NumericFeature('discontinued_year', { nice: false }),
    extendable_hood: new BooleanFeature('extendable_hood'),
    foldable_carrycot: new VariantBooleanListJsonFeature('foldable_carrycot'),
    folded_dimensions_in_cm: new MinMaxDimensionsFeature('folded_dimensions_in_cm'),
    folding_system: new FoldingSystemFeature('folding_system'),
    folds_by_itself: new BooleanFeature('folds_by_itself'),
    folds_with_the_seat_attached: new BooleanFeature('folds_with_the_seat_attached'),
    folds_with_the_seat_in_reverse: new BooleanFeature('folds_with_the_seat_in_reverse'),
    foot_rest: new BooleanFeature('foot_rest'),
    front_wheel_type: new WheelTypeFeature('front_wheel_type'),
    front_wheels_diameter_in_cm: new OrNumericListFeature('front_wheels_diameter_in_cm_l'),
    fully_fledged_second_seat: new BooleanFeature('fully_fledged_second_seat'),
    handle_height_in_cm: new NumericOptionalRangeFeature('handle_height_in_cm'),
    handle_material: new MaterialFeature('handle_material'),
    has_carry_handle: new BooleanFeature('has_carry_handle'),
    height_adjustable_seat: new BooleanFeature('height_adjustable_seat'),
    higher_seat_position: new BooleanFeature('higher_seat_position'),
    hood_features_a_sun_visor: new BooleanFeature('hood_features_a_sun_visor'),
    inclinable_carrycot: new VariantBooleanListJsonFeature('inclinable_carrycot'),
    led_lighting: new BooleanFeature('led_lighting'),
    leg_rest_can_be_extended: new BooleanFeature('leg_rest_can_be_extended'),
    leg_rest_can_be_positioned: new BooleanFeature('leg_rest_can_be_positioned'),
    leg_rest_positions: new NumericFeature('leg_rest_positions'),
    manual_brake_present: new BooleanFeature('manual_brake_present'),
    maximum_load_per_1st_child_in_kg: new NumericFeature('maximum_load_per_1st_child_in_kg'),
    maximum_load_per_2nd_child_in_kg: new NumericFeature('maximum_load_per_2nd_child_in_kg'),
    maximum_load_per_child_in_kg: new NumericFeature('maximum_load_per_child_in_kg'),
    memory_button: new BooleanFeature('memory_button'),
    missing_sitting_part: new BooleanFeature('missing_sitting_part'),
    model_url: new Feature('model_url'),
    motorized: new BooleanFeature('motorized'),
    number_of_double_wheels: new NumericFeature('number_of_double_wheels'),
    number_of_wheels: new NumericListFeature('number_of_wheels_l', { sorted: false }),
    one_hand_folding: new BooleanFeature('one_hand_folding'),
    one_hand_positioning: new BooleanFeature('one_hand_positioning'),
    origin_countries: new CountriesFeature('origin_countries'),
    parent_tray_organizer: new BooleanFeature('parent_tray_organizer'),
    platform_material: new MaterialFeature('platform_material'),
    possibility_of_terrain_wheels: new BooleanFeature('possibility_of_terrain_wheels'),
    pramette_seat_2in1: new BooleanFeature('pramette_seat_2in1'),
    rear_wheel_type: new WheelTypeFeature('rear_wheel_type'),
    rear_wheels_diameter_in_cm: new OrNumericListFeature('rear_wheels_diameter_in_cm_l'),
    recommended_age: new NumericOptionalRangeFeature('recommended_age'),
    retroreflector: new BooleanFeature('retroreflector'),
    roof_present: new BooleanFeature('roof_present'),
    rotating_seat: new BooleanFeature('rotating_seat'),
    rotating_second_seat: new BooleanFeature('rotating_second_seat'),
    rotating_wheels: new BooleanFeature('rotating_wheels'),
    seat_dims_in_cm: new WidthDepthFeature('seat_dims_in_cm'),
    seat_fabrics_can_be_taken_off: new BooleanFeature('seat_fabrics_can_be_taken_off'),
    seat_safety_indicator: new BooleanFeature('seat_safety_indicator'),
    seat_surface_total_length_in_cm: new NumericOptionalRangeFeature('seat_surface_total_length_in_cm'),
    second_seat_back_support_can_be_in_lying_position: new BooleanFeature('second_seat_back_support_can_be_in_lying_position'),
    second_seat_back_support_can_be_positioned: new BooleanFeature('second_seat_back_support_can_be_positioned'),
    second_seat_back_support_length_in_cm: new NumericOptionalRangeFeature('second_seat_back_support_length_in_cm'),
    second_seat_back_support_positioning_type: new BackSupportPositioningTypeFeature('second_seat_back_support_positioning_type'),
    second_seat_back_support_positions: new InfiniteNumericListFeature('second_seat_back_support_positions_l', { infinityName: STROLLER_INFINITE_RECLINE }),
    second_seat_bumper_bar: new BooleanFeature('second_seat_bumper_bar'),
    second_seat_dims_in_cm: new WidthDepthFeature('second_seat_dims_in_cm'),
    second_seat_foot_rest: new BooleanFeature('second_seat_foot_rest'),
    second_seat_leg_rest: new BooleanFeature('second_seat_leg_rest'),
    second_seat_one_hand_positioning: new BooleanFeature('second_seat_one_hand_positioning'),
    second_seat_recommended_age: new NumericOptionalRangeFeature('second_seat_recommended_age'),
    second_seat_roof_present: new BooleanFeature('second_seat_roof_present'),
    second_seat_surface_total_length_in_cm: new NumericOptionalRangeFeature('second_seat_surface_total_length_in_cm'),
    shopping_basket: new BooleanFeature('shopping_basket'),
    shopping_basket_capacity_in_kg: new NumericFeature('shopping_basket_capacity_in_kg'),
    shopping_basket_closable: new BooleanFeature('shopping_basket_closable'),
    sportgolf_only: new BooleanFeature('sportgolf_only'),
    standing_fold: new BooleanFeature('standing_fold'),
    supplied_cape: new BooleanFeature('supplied_cape'),
    supplied_changing_bag: new SuppliedChangingBagFeature('supplied_changing_bag'),
    supplied_footmuff: new BooleanFeature('supplied_footmuff'),
    supplied_mosquito_net: new BooleanFeature('supplied_mosquito_net'),
    supplied_pad: new BooleanFeature('supplied_pad'),
    supplied_transport_bag: new BooleanFeature('supplied_transport_bag'),
    supplied_wheel_protective_sleeves: new BooleanFeature('supplied_wheel_protective_sleeves'),
    suspended_wheels: new SuspendedWheelsFeature('suspended_wheels'),
    suspension: new BooleanFeature('suspension'),
    suspension_type: new SuspensionTypeFeature('suspension_type'),
    swing_away_bumper_bar: new BooleanFeature('swing_away_bumper_bar'),
    swinging: new BooleanFeature('swinging'),
    two_car_seats_can_be_attached: new BooleanFeature('two_car_seats_can_be_attached'),
    type: new TypeFeature('type'),
    type_of_handle_adjustment: new HandleAdjustmentTypeFeature('type_of_handle_adjustment'),
    unfolded_dimensions_in_cm: new MinMaxDimensionsFeature('unfolded_dimensions_in_cm'),
    video_url: new Feature('video_url'),
    weight_in_kg: new NumericOptionalRangeFeature('weight_in_kg'),
    wheel_direction_memory: new BooleanFeature('wheel_direction_memory'),
    wheel_mudguards: new BooleanFeature('wheel_mudguards'),
    wheels_can_be_arrested: new BooleanFeature('wheels_can_be_arrested'),
    wheels_can_be_removed: new BooleanFeature('wheels_can_be_removed'),
    wipe_clean_leg_rest: new BooleanFeature('wipe_clean_leg_rest'),
};
